import { useEffect, useRef, useState } from 'react';
/**
 * How long the worklist waits after the last keystroke in a free-text filter
 * before it queries. Matches the Teleconsultation toolbar's search debounce.
 */
export const WORKLIST_TEXT_FILTER_DEBOUNCE_MS = 300;
// Free-text filters the user types into. Each keystroke lands in the URL
// immediately (so the box stays responsive and the URL stays shareable), but
// the list query must not follow per keystroke: a text filter can force a
// full scan on the server, and one scan per character piles up concurrent
// scans.
const TEXT_FILTER_KEYS = [
    'search',
    'patientName',
    'referringPhysiciansName',
    'institutionName',
    'reportKeyword',
    'tag',
];
function sameText(a, b) {
    return TEXT_FILTER_KEYS.every((key) => (a[key] ?? '') === (b[key] ?? ''));
}
/**
 * The filters to actually query with: `filters` itself, except that while a
 * free-text filter is still being typed the previous settled filters are
 * returned until the input has been idle for `delayMs`. Changes that do not
 * touch a text filter (page, sort, dropdowns, folders) apply immediately —
 * unless a text edit is still pending, in which case they ride along with it
 * when the pause ends. That is deliberate: applying them at once would send a
 * query pairing the new dropdown with text the user has already changed, only
 * to supersede it moments later — the extra scan this hook exists to avoid.
 */
export function useDebouncedWorklistFilters(filters, delayMs = WORKLIST_TEXT_FILTER_DEBOUNCE_MS) {
    const [settled, setSettled] = useState(filters);
    const settledRef = useRef(settled);
    useEffect(() => {
        if (sameText(filters, settledRef.current)) {
            // No text change in flight (or the user typed back to the settled value).
            if (filters !== settledRef.current) {
                settledRef.current = filters;
                setSettled(filters);
            }
            return;
        }
        const timer = setTimeout(() => {
            settledRef.current = filters;
            setSettled(filters);
        }, delayMs);
        return () => clearTimeout(timer);
    }, [filters, delayMs]);
    return settled;
}
